import {
  type CreateParishExpenseRequest,
  type CreateParishIncomeRequest,
  type ListParishTransactionsQuery,
  type ListParishTransactionsResponse,
  type ParishTransactionResponse,
} from "@/features/finance/model/finance-types"
import {
  http,
} from "@/shared/lib/api/http"
import {
  extractListPayload,
  normalizeNumber,
  normalizeString,
} from "./finance-api-shared"
import {
  normalizeTransactionRecord,
} from "./finance-transactions-api"


function normalizeParishTransactionResponse(input: unknown, fallbackWorkspaceId: string): ParishTransactionResponse {
  const record = normalizeTransactionRecord(input)
  return {
    id: record.id,
    workspaceId: record.workspaceId || fallbackWorkspaceId,
    branchId: record.branchId,
    accountId: record.accountId,
    transactionReference: record.transactionReference,
    type: record.type,
    amount: record.amount,
    accountingAccountId: record.accountingAccountId,
    activityId: record.activityId,
    paymentMethod: record.paymentMethod,
    paymentReference: record.paymentReference,
    description: record.description,
    occurredAt: record.occurredAt,
    status: record.status,
    account: record.account,
    lines: record.lines,
    accountingAccount: record.accountingAccount,
    activity: record.activity ? { id: record.activity.id, title: record.activity.title } : null,
    createdAt: record.createdAt,
    updatedAt: record.updatedAt,
  }
}


function buildParishTransactionsParams(query: ListParishTransactionsQuery): Record<string, string | number> {
  const params: Record<string, string | number> = {
    workspaceId: query.workspaceId,
    includeReversals: query.includeReversals === true ? "true" : "false",
  }

  if (query.branchId) params.branchId = query.branchId
  if (query.incomeCategoryId) params.incomeCategoryId = query.incomeCategoryId
  if (query.expenseCategoryId) params.expenseCategoryId = query.expenseCategoryId
  if (query.activityId) params.activityId = query.activityId
  if (query.type) params.type = query.type
  if (query.status) params.status = query.status
  if (query.dateFrom) params.dateFrom = query.dateFrom
  if (query.dateTo) params.dateTo = query.dateTo
  if (query.search) params.search = query.search
  if (typeof query.page === "number" && query.page > 0) params.page = query.page
  if (typeof query.limit === "number" && query.limit > 0) params.limit = Math.min(query.limit, 100)
  if (query.sortBy) params.sortBy = query.sortBy
  if (query.sortOrder) params.sortOrder = query.sortOrder

  return params
}


function normalizeListParishTransactionsResponse(
  input: unknown,
  query: ListParishTransactionsQuery,
): ListParishTransactionsResponse {
  const record = (input ?? {}) as Record<string, unknown>
  const items = extractListPayload(input, ["items", "transactions"], (value) =>
    normalizeParishTransactionResponse(value, query.workspaceId),
  )

  return {
    workspaceId: normalizeString(record.workspaceId ?? record.workspace_id) ?? query.workspaceId,
    parishBranchId:
      normalizeString(
        record.parishBranchId ?? record.parish_branch_id ?? record.branchId ?? record.branch_id,
      ) ?? "",
    items,
    total: normalizeNumber(record.total) ?? items.length,
    page: normalizeNumber(record.page) ?? query.page ?? 1,
    limit: normalizeNumber(record.limit) ?? query.limit ?? 20,
  }
}


export async function listFinanceParishTransactions(
  query: ListParishTransactionsQuery,
): Promise<ListParishTransactionsResponse> {
  const response = await http.get<unknown>("/finance/parish/transactions", {
    params: buildParishTransactionsParams(query),
  })

  return normalizeListParishTransactionsResponse(response.data, query)
}


export async function createFinanceParishIncome(
  input: CreateParishIncomeRequest,
): Promise<ParishTransactionResponse> {
  const response = await http.post<unknown>("/finance/parish/income", {
    workspaceId: input.workspaceId,
    ...(typeof input.branchId !== "undefined" ? { branchId: input.branchId } : {}),
    incomeCategoryId: input.incomeCategoryId,
    ...(typeof input.accountId !== "undefined" ? { accountId: input.accountId } : {}),
    amount: input.amount,
    ...(typeof input.occurredAt !== "undefined" ? { occurredAt: input.occurredAt } : {}),
    ...(typeof input.paymentMethod !== "undefined" ? { paymentMethod: input.paymentMethod } : {}),
    ...(typeof input.paymentReference !== "undefined" ? { paymentReference: input.paymentReference } : {}),
    ...(typeof input.description !== "undefined" ? { description: input.description } : {}),
  })

  return normalizeParishTransactionResponse(response.data, input.workspaceId)
}


export async function createFinanceParishExpense(
  input: CreateParishExpenseRequest,
): Promise<ParishTransactionResponse> {
  const response = await http.post<unknown>("/finance/parish/expense", {
    workspaceId: input.workspaceId,
    ...(typeof input.branchId !== "undefined" ? { branchId: input.branchId } : {}),
    ...(typeof input.accountId !== "undefined" ? { accountId: input.accountId } : {}),
    expenseCategoryId: input.expenseCategoryId,
    fundId: input.fundId,
    amount: input.amount,
    ...(typeof input.activityId !== "undefined" ? { activityId: input.activityId } : {}),
    ...(typeof input.occurredAt !== "undefined" ? { occurredAt: input.occurredAt } : {}),
    ...(typeof input.paymentMethod !== "undefined" ? { paymentMethod: input.paymentMethod } : {}),
    ...(typeof input.paymentReference !== "undefined" ? { paymentReference: input.paymentReference } : {}),
    ...(typeof input.description !== "undefined" ? { description: input.description } : {}),
  })

  return normalizeParishTransactionResponse(response.data, input.workspaceId)
}
